import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import { addAnalyticsEventListeners } from 'analytics/domEvents'
import {
  addEvents,
  createSession,
  startAnalyticsEventFlushInterval
} from 'analytics/service'
import {
  getAccessToken,
  getCurrrentSession,
  trackSource
} from 'analytics/utils'

import { analytics as analyticsPlugin } from '../analytics/plugin'
import type {
  AnalyticsContextType,
  InitializeAnalyticsPayload,
  PortalAnalyticsPlugin
} from '../analytics/types'

import AuthContext from './AuthContext'
import PortalConfigContext from './PortalConfigContext'

const AnalyticsContext = createContext<AnalyticsContextType | null>(null)

trackSource()

export const AnalyticsProvider = ({ children }) => {
  const [analytics, setAnalytics] = useState<PortalAnalyticsPlugin | null>(null)
  const {
    data: { portalConfig }
  } = useContext(PortalConfigContext)
  const { data: authConfig } = useContext(AuthContext)

  useEffect(() => {
    handleExistingSession()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalConfig])

  // Stop the existing session if we no longer have an authenticated user
  useEffect(() => {
    const session = getCurrrentSession()

    if (!authConfig?.authenticated && analytics && session) {
      analytics.endSession()
    }
  }, [analytics, authConfig?.authenticated])

  const handleExistingSession = async () => {
    try {
      const session = getCurrrentSession()
      const accessToken = await getAccessToken()

      if (accessToken && !session) {
        initializeAnalytics({
          appName: portalConfig?.origin,
          email: authConfig?.user?.attributes?.email,
          userActivity: null,
          publicToken: portalConfig?.accessToken
        })
      } else if (session) {
        setAnalytics(analyticsPlugin?.plugins?.portalAnalyticsPlugin || null)
        startAnalyticsEventFlushInterval()
        addAnalyticsEventListeners()
      }
    } catch (err) {
      console.error('Error handling existing session', err)
    }
  }

  const initializeAnalytics = useCallback(
    async (initAnalyticsPayload: InitializeAnalyticsPayload) => {
      try {
        const { appName, email, userActivity, publicToken } =
          initAnalyticsPayload
        const analytics =
          analyticsPlugin?.plugins?.portalAnalyticsPlugin || null
        const existingSession = getCurrrentSession()

        // During registration, access token will not be available, so pass public token explicitly from invoker method and store it in local storage
        if (publicToken) {
          localStorage.setItem('publicToken', publicToken)
        }

        if (analytics && !existingSession) {
          addAnalyticsEventListeners()
          await createSession({
            appName,
            email
          })
          startAnalyticsEventFlushInterval()
          if (userActivity) {
            addEvents([
              {
                type: userActivity,
                details: { email },
                timestamp: new Date().toISOString()
              }
            ])
          }
          setAnalytics(analytics)
        }
      } catch (err) {
        console.error('Error initializing analytics', err)
      }
    },
    []
  )

  const value = useMemo(
    () => ({ analytics, initializeAnalytics }),
    [analytics, initializeAnalytics]
  )

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  )
}

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext)

  return context
}
